import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { closeOutGateReasons, tasks } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { cn } from "../../lib/cn";
import { EvidenceBody } from "./EvidencePanel";
import { notify } from "../../lib/notifications";
import type {
  ConfirmDoneResult,
  DoneFlowResult,
  MergeBranchResult,
  Task,
} from "../../lib/types";

interface Props {
  task: Task;
  onClose: () => void;
}

/**
 * Reviewer's terminal step. Two paths:
 *   - "Mark done" → POST /confirm-done. A standalone branch is queued for
 *     the merge queue (rebase on main, gates again, fast-forward, Maestro
 *     pushes and writes the CHANGELOG). A track task merges directly. The
 *     merge gate needs a verified HEAD; otherwise the operator overrides
 *     with a written reason, recorded as evidence.
 *   - "Send back" → required textarea, POST /send-back, task flips to TODO.
 */
export function DoneReviewModal({ task, onClose }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"choose" | "send-back" | "override" | "result">("choose");
  const [feedback, setFeedback] = useState("");
  const [reason, setReason] = useState("");
  const verification = useQuery({
    queryKey: ["task-verification", task.id],
    queryFn: () => tasks.verification(task.id),
  });
  // Mark done needs a verified HEAD; anything else is an explicit override.
  const blocked = verification.data ? !verification.data.mergeable : false;
  const [result, setResult] = useState<ConfirmDoneResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const confirm = useMutation({
    mutationFn: (override?: { override: boolean; reason: string }) =>
      tasks.confirmDone(task.id, override),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      setMode("result");
      qc.invalidateQueries({ queryKey: ["kanban"] });
      qc.invalidateQueries({ queryKey: ["task", task.id] });
      // Persist the outcome past the modal — a failed merge / push-pending /
      // docs error stays visible in the toast stack after the operator closes.
      notifyConfirmOutcome(task.title, r);
    },
    onError: (e) => {
      // The gate refused after the evidence we showed went stale (a new
      // commit landed): show its reasons and offer the override form.
      const reasons = closeOutGateReasons(e);
      if (reasons) {
        verification.refetch();
        setError(reasons);
        setMode("override");
        return;
      }
      const msg = e instanceof Error ? e.message : "Mark done failed";
      setError(msg);
      notify({
        severity: "error",
        title: `Mark done failed — ${task.title}`,
        detail: msg,
      });
    },
  });

  const sendBack = useMutation({
    mutationFn: () => tasks.sendBack(task.id, feedback),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      qc.invalidateQueries({ queryKey: ["task", task.id] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "Send-back failed";
      setError(msg);
      notify({
        severity: "error",
        title: `Send-back failed — ${task.title}`,
        detail: msg,
      });
    },
  });

  const hasBranch = Boolean(task.worktree_name);
  const alreadyMerged = Boolean(task.merged_at);

  return (
    <Modal
      open
      onClose={onClose}
      title={
        mode === "send-back"
          ? "Send task back to TODO"
          : mode === "override"
            ? "Override verification"
            : "Mark task as done?"
      }
      description={task.title}
      size="md"
      footer={
        mode === "choose" ? (
          <>
            <Button variant="ghost" onClick={() => setMode("send-back")}>
              Send back…
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {blocked && (
              <Button variant="secondary" onClick={() => setMode("override")}>
                Override…
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => confirm.mutate(undefined)}
              disabled={confirm.isPending || blocked || verification.isLoading}
              title={
                blocked
                  ? "Not verified on its latest commit — override with a reason, or send it back"
                  : "Queue the branch to merge; Maestro writes the CHANGELOG"
              }
            >
              {confirm.isPending ? "Closing out…" : "Mark done"}
            </Button>
          </>
        ) : mode === "override" ? (
          <>
            <Button variant="ghost" onClick={() => setMode("choose")}>
              Back
            </Button>
            <div className="flex-1" />
            <Button
              variant="primary"
              onClick={() => confirm.mutate({ override: true, reason: reason.trim() })}
              disabled={!reason.trim() || confirm.isPending}
            >
              {confirm.isPending ? "Closing out…" : "Override and mark done"}
            </Button>
          </>
        ) : mode === "send-back" ? (
          <>
            <Button variant="ghost" onClick={() => setMode("choose")}>
              Back
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => sendBack.mutate()}
              disabled={!feedback.trim() || sendBack.isPending}
            >
              {sendBack.isPending ? "Sending…" : "Send back to TODO"}
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1" />
            {result?.merge?.status === "conflict" && (
              <Button
                variant="secondary"
                onClick={() => confirm.mutate(undefined)}
                disabled={confirm.isPending}
                title="Re-run the merge into main now that you've resolved the conflict on the worker"
              >
                {confirm.isPending ? "Retrying merge…" : "Retry merge"}
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </>
        )
      }
    >
      {mode === "override" && (
        <div className="space-y-2">
          <ul className="space-y-0.5 text-small text-status-error">
            {(verification.data?.blocked ?? []).map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <label className="text-caption text-text-tertiary" htmlFor="override-reason">
            Why is it safe to merge anyway? Saved with the task's evidence.
          </label>
          <textarea
            id="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={cn(
              "w-full px-3 py-2 bg-bg-inset rounded-md min-h-[80px]",
              "border border-transparent hover:border-border-default focus:border-border-strong",
              "text-body text-text-primary placeholder:text-text-quaternary",
              "outline-none resize-y transition-colors duration-fast ease-out",
            )}
            autoFocus
          />
          {error && <div className="text-small text-status-error">{error}</div>}
        </div>
      )}

      {mode === "choose" && verification.data && (
        <div className="mb-4">
          <EvidenceBody v={verification.data} />
        </div>
      )}

      {mode === "choose" && (
        <div className="space-y-3 text-small text-text-secondary">
          <p>
            {hasBranch && !alreadyMerged ? (
              <>
                <code>agent/{task.worktree_name}</code> is queued to land on
                main: rebased, gated again, fast-forwarded and pushed by
                Maestro, which then writes the CHANGELOG entry.
              </>
            ) : hasBranch ? (
              <>The branch is already on main; Maestro writes the CHANGELOG entry.</>
            ) : (
              <>Maestro writes the CHANGELOG entry and pushes.</>
            )}
          </p>
          {task.send_back_count > 0 && (
            <div className="text-caption text-status-paused">
              This task has been sent back {task.send_back_count} time
              {task.send_back_count === 1 ? "" : "s"} previously.
            </div>
          )}
          {error && (
            <div className="text-small text-status-error">{error}</div>
          )}
        </div>
      )}

      {mode === "send-back" && (
        <div className="space-y-2">
          <label
            className="text-caption text-text-tertiary"
            htmlFor="send-back-feedback"
          >
            What's missing? (Will be prepended to the prompt on the next dispatch.)
          </label>
          <textarea
            id="send-back-feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="e.g. The migration ran but the API still 500s — investigate /api/foo."
            className={cn(
              "w-full px-3 py-2 bg-bg-inset rounded-md min-h-[100px]",
              "border border-transparent hover:border-border-default focus:border-border-strong",
              "text-body text-text-primary placeholder:text-text-quaternary",
              "outline-none resize-y transition-colors duration-fast ease-out",
            )}
            autoFocus
          />
          {error && (
            <div className="text-small text-status-error">{error}</div>
          )}
        </div>
      )}

      {mode === "result" && result && (
        <div className="space-y-4">
          {result.merge && <MergeResultBody result={result.merge} />}
          {result.merge?.status !== "queued" && <ResultBody result={result.done} />}
        </div>
      )}
    </Modal>
  );
}

/**
 * Translate a confirm-done result into a persistent toast. The merge gates
 * everything, so it's read first: a conflict/error means the work never
 * landed on main (task parked at Needs Input) — surface that loudly. Otherwise
 * the docs round-trip decides: error, push-pending, or a clean success.
 */
function notifyConfirmOutcome(title: string, r: ConfirmDoneResult) {
  const merge = r.merge;
  if (merge && (merge.status === "conflict" || merge.status === "error")) {
    notify({
      severity: merge.status === "conflict" ? "warning" : "error",
      title:
        merge.status === "conflict"
          ? `Mark done — merge conflict: ${title}`
          : `Mark done — merge failed: ${title}`,
      detail: merge.message,
    });
    return;
  }
  if (r.done.status === "error") {
    notify({
      severity: "error",
      title: `Mark done — docs round-trip failed: ${title}`,
      detail: r.done.message,
    });
    return;
  }
  if (r.done.status === "partial") {
    notify({
      severity: "warning",
      title: `Marked done — committed, push pending: ${title}`,
      detail: r.done.message,
    });
    return;
  }
  notify({ severity: "success", title: `Marked done: ${title}` });
}

function MergeResultBody({ result }: { result: MergeBranchResult }) {
  const palette: Record<MergeBranchResult["status"], string> = {
    ok: "var(--status-success)",
    skipped: "var(--text-tertiary)",
    partial: "var(--status-paused)",
    conflict: "var(--status-warning)",
    error: "var(--status-error)",
    queued: "var(--status-running)",
  };
  const label =
    result.status === "ok"
      ? "merged"
      : result.status === "queued"
        ? "queued to merge"
      : result.status === "partial"
        ? "merged, push pending"
        : result.status === "skipped"
          ? "skipped"
          : result.status === "conflict"
            ? "conflict — not merged"
            : "failed";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: palette[result.status] }}
          aria-hidden="true"
        />
        <span className="text-small text-text-secondary">
          Merge to main: {label}
        </span>
      </div>
      <pre
        className={cn(
          "text-mono text-text-secondary whitespace-pre-wrap break-words",
          "bg-bg-inset border border-border-subtle rounded-md p-3 max-h-[30vh] overflow-auto",
        )}
      >
        {result.message}
      </pre>
      {result.status === "conflict" && (
        <p className="text-small text-text-secondary">
          The task moved to <strong>Needs Input</strong> — the work is still
          on its branch. Resolve the conflict on the worker, then click{" "}
          <strong>Retry merge</strong> below.
        </p>
      )}
    </div>
  );
}

function ResultBody({ result }: { result: DoneFlowResult }) {
  const palette: Record<DoneFlowResult["status"], string> = {
    ok: "var(--status-success)",
    skipped: "var(--text-tertiary)",
    partial: "var(--status-paused)",
    error: "var(--status-error)",
  };
  return (
    <div className="space-y-3 border-t border-border-subtle pt-3">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: palette[result.status] }}
          aria-hidden="true"
        />
        <span className="text-small text-text-secondary capitalize">
          {result.status === "ok"
            ? "Docs round-trip complete"
            : result.status === "partial"
              ? "Committed, push pending"
              : result.status === "skipped"
                ? "No doc changes were necessary"
                : "Docs round-trip failed"}
        </span>
      </div>
      <ul className="text-small text-text-tertiary space-y-1 list-disc list-inside">
        <li>
          PENDING.md edited: {result.pending_edited ? "yes" : "no"}
        </li>
        <li>
          CHANGELOG.md appended: {result.changelog_appended ? "yes" : "no"}
        </li>
        <li>Committed: {result.committed ? "yes" : "no"}</li>
        <li>Pushed: {result.pushed ? "yes" : "no"}</li>
      </ul>
      <pre
        className={cn(
          "text-mono text-text-secondary whitespace-pre-wrap break-words",
          "bg-bg-inset border border-border-subtle rounded-md p-3 max-h-[40vh] overflow-auto",
        )}
      >
        {result.message}
      </pre>
    </div>
  );
}
